import React from 'react';
import { batch } from 'react-redux';
import { NextPage } from 'next';
import router from 'next/router';
import StockTrimPage from '@components/TrimPage/Stock';
import { FeatureSwitchesContext } from '../../../types/featureSwitches';
import { handleInitialURLParams } from '@utils/page.utils';
import { ContextWithStore } from '@utils/getInitialProps/@types';
import StockService from '../../../services/stock/stock.service';
import {
    filtersFromUrl,
    getGeolocationByQueryParameters,
    getGeolocationNameByQueryParameters,
    getGeoLocationPropertiesFromURL,
    stockCarFiltersExportToParam,
} from '../../../services/filters/utils/Filters.utils';
import { getFilterCategories, getStockFilters } from '../../../services/filters/filterHelpers';
import FilterDuck from '../../../redux/filters/filter.duck';
import globalDuck from '../../../redux/global/global.duck';
import { CarJourneyType, dealerApi } from '../../../services';
import { SortOrder, SortType, StockType } from '../../../services/stock/models/stockItems.service.model';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { updateGeneralContext } from '@utils/getInitialProps/common';
import ContentDuck from '../../../redux/content/content.duck';
import { NEED_HAND_SETTING_KEY } from 'src/constants/main';
import navigationDuck from 'src/redux/navigation/navigation.duck';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import DealerDuck from '../../../redux/dealer/dealer.duck';
import { IDealer } from '../../../interfaces/Dealer';
import { validateDealerId } from '@utils/Deal.utils';
import UserDuck from '../../../redux/user/user.duck';
import isEmpty from 'lodash/isEmpty';
import { EUserLoginType } from 'src/interfaces/User';
import { STOCK_TYPE } from '../../../services/filters/filters';

interface ISelectorProps {
    nameplateBodyStyleSlug: string;
}

const TrimPage: NextPage<ISelectorProps> = () => {
    return <StockTrimPage />;
};

export const INITIAL_PAGE_NUMBER = 1;

TrimPage.getInitialProps = async (ctx: FeatureSwitchesContext<ContextWithStore>): Promise<any> => {
    let statusCode: number = undefined;

    const { store, res, asPath, featureContext, query, req } = await updateGeneralContext(ctx, {
        headers: [
            {
                key: 'Cache-Control',
                value: `private, no-cache`,
            },
        ],
    });
    const { model } = query;

    const nameplateBodyStyleSlug = model[0];
    const modelId = model[1];
    const bodyStyleId = model[2];

    const DESERIALIZATION_ERROR = 'DeSerializationError';

    try {
        handleInitialURLParams(store, asPath, res, router, featureContext);

        const userAuth = UserDuck.getOwnState(store.getState()).userAuth;

        const currentState = store.getState();

        const resultsPerPage = TrimSelectorDuck.getOwnState(currentState).stock.itemsPerPage;
        const storeGeoLocation = FilterDuck.getOwnState(currentState).geoLocation;
        const settings = ContentDuck.getSettings(currentState);

        const needHandBlockOrder = Number(
            settings.find((setting) => setting.key === NEED_HAND_SETTING_KEY)?.value || 0
        );

        // #region ALL CARS REQUEST
        const [responseAllVehicles, responseDemoVehicles] = await Promise.all([
            StockService.getStockVehicles(
                modelId,
                bodyStyleId,
                INITIAL_PAGE_NUMBER,
                needHandBlockOrder > 0 ? resultsPerPage - 1 : 0,
                {
                    ...(query[STOCK_TYPE] && { [STOCK_TYPE]: query[STOCK_TYPE] }),
                }
            ),
            StockService.getStockVehicles(
                modelId,
                bodyStyleId,
                INITIAL_PAGE_NUMBER,
                needHandBlockOrder > 0 ? resultsPerPage - 1 : 0,
                {
                    [STOCK_TYPE]: StockType.VD,
                }
            ),
        ]);

        if (responseAllVehicles) {
            const allFilters = responseAllVehicles.data?.filters;

            let { geoLocation, distanceRadius, geoLocationName } = getGeoLocationPropertiesFromURL(query);
            let dealer: IDealer = DealerDuck.getOwnState(currentState).dealer;

            geoLocation = geoLocation || FilterDuck.getOwnState(currentState).geoLocation;
            geoLocationName = geoLocationName || FilterDuck.getOwnState(currentState).geoLocationName;
            distanceRadius = distanceRadius ?? FilterDuck.getOwnState(currentState).distanceRadius;

            if (!storeGeoLocation && geoLocation) {
                query.sort = `${SortType.PROXIMITY},${SortOrder.ASC}`;
            }

            const parsedFilters = filtersFromUrl(getStockFilters(allFilters), query, allFilters);

            const dealerId = query.idsitegeo as string;
            const isDealerIdValid = validateDealerId(dealerId);

            const currentDealerExternalId = DealerDuck.getOwnState(store.getState()).dealer?.externalId;
            const shouldFetchDealerData = isDealerIdValid && currentDealerExternalId !== dealerId;

            if (shouldFetchDealerData && dealerId !== dealer?.externalId) {
                dealer = (await dealerApi.getDealer(dealerId))?.data;
            }
            const isAgentDealer = userAuth.loginType === EUserLoginType.DEALER && userAuth?.detail?.dealerCode;
            const responseAvailableVehicles = await StockService.getStockVehicles(
                modelId,
                bodyStyleId,
                INITIAL_PAGE_NUMBER,
                needHandBlockOrder > 0 ? resultsPerPage - 1 : resultsPerPage,
                {
                    ...stockCarFiltersExportToParam(parsedFilters),
                    ...(dealerId && { rrdi7: dealer?.attachedNVActorCode }),
                    ...(!dealerId && isAgentDealer && { rrdi7: userAuth?.detail?.dealerCode }),
                },
                dealerId ? { lat: dealer?.latitude, lng: dealer?.longitude } : geoLocation,
                dealerId ? 0 : distanceRadius
            );

            batch(() => {
                store.dispatch(
                    FilterDuck.setFilters(
                        parsedFilters,
                        getGeolocationByQueryParameters(geoLocation, dealer),
                        distanceRadius,
                        getGeolocationNameByQueryParameters(geoLocation, dealer, geoLocationName),
                        isDealerIdValid ? dealerId : null
                    )
                );
                store.dispatch(navigationDuck.setModelRedirectUrl(asPath.split('?')[0]));
                store.dispatch(
                    FilterDuck.setFilteredFilters(
                        getStockFilters(responseAvailableVehicles.data?.filters ?? allFilters)
                    )
                );
                store.dispatch(FilterDuck.setFilterCategories(getFilterCategories(allFilters)));
                if (dealerId && !isEmpty(dealer)) {
                    store.dispatch(DealerDuck.setDealer(dealer));
                }
                store.dispatch(
                    FilterDuck.setPriceRange({
                        min: allFilters.minBudget?.value,
                        max: allFilters.maxBudget?.value,
                        availableMin: allFilters.minBudget?.value,
                        availableMax: allFilters.maxBudget?.value,
                    })
                );
                store.dispatch(globalDuck.setCarJourney(CarJourneyType.STOCK));
                store.dispatch(
                    FilterDuck.changeBudgetType(
                        featureContext[FEATURES_LIST.FEATURE_SWITCH_DEFAULT_PAYMENT_JOURNEY_STOCK]
                    )
                );
                store.dispatch(FilterDuck.setCheckDemoCars(responseDemoVehicles?.data?.totalVehiclesCount > 0));
                store.dispatch(
                    TrimSelectorDuck.setStockTrims({
                        stockTrims: responseAllVehicles.data?.vehicles,
                        totalItems: responseAllVehicles.data?.totalVehiclesCount,
                        pageNumber: INITIAL_PAGE_NUMBER,
                        filteredStockTrims: responseAvailableVehicles.data?.vehicles,
                        totalFilteredItems: responseAvailableVehicles.data?.totalVehiclesCount,
                        areAvailableDemoTrims: responseDemoVehicles?.data?.totalVehiclesCount > 0,
                    })
                );
            });
        } else {
            statusCode = 503;
        }

        return { statusCode, namespacesRequired: ['common'] };
    } catch (error: any) {
        if (error?.name === DESERIALIZATION_ERROR) {
            store.dispatch(TrimSelectorDuck.setFilteredTrims([]));
        }
    }
    return { query, nameplateBodyStyleSlug, modelId, bodyStyleId, namespacesRequired: ['common'] };
};

export default TrimPage;
